import { useCallback, useEffect, useState } from "react";
import { apiFetch } from "@/lib/api-client";
import type { CrmActionObservedOutcome } from "./use-crm-event-action-score";

export interface CrmActionOutcomeLeaderboardItem extends CrmActionObservedOutcome {
  actionRef: string;
  intent: string;
}

export interface CrmActionOutcomeLeaderboardFilters {
  actionRef?: string;
  contractRef?: string;
  sourceEventType?: string;
  signalFamily?: string;
}

export interface CrmActionOutcomeLeaderboard {
  windowDays: number;
  filters: {
    actionRef: string | null;
    contractRef: string | null;
    sourceEventType: string | null;
    signalFamily: string | null;
  };
  items: CrmActionOutcomeLeaderboardItem[];
}

export function useCrmActionOutcomeLeaderboard(filters: CrmActionOutcomeLeaderboardFilters = {}) {
  const [data, setData] = useState<CrmActionOutcomeLeaderboard | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchLeaderboard = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      const actionRef = filters.actionRef ?? filters.contractRef;
      if (actionRef) {
        params.set("actionRef", actionRef);
      }
      if (filters.sourceEventType) {
        params.set("sourceEventType", filters.sourceEventType);
      }
      if (filters.signalFamily) {
        params.set("signalFamily", filters.signalFamily);
      }
      const query = params.toString();
      const response = await apiFetch<{ data: CrmActionOutcomeLeaderboard }>(
        `/crm/intelligence/action-recommendations/outcomes/leaderboard${query ? `?${query}` : ""}`,
      );
      setData(response.data);
    } catch (caughtError) {
      setError(caughtError instanceof Error ? caughtError.message : "Failed to load action outcome leaderboard");
    } finally {
      setIsLoading(false);
    }
  }, [filters.actionRef, filters.contractRef, filters.signalFamily, filters.sourceEventType]);

  useEffect(() => {
    fetchLeaderboard();
  }, [fetchLeaderboard]);

  return { data, isLoading, error, refetch: fetchLeaderboard };
}
